/**
 * EchoForge authentication via Logto OIDC.
 * Wraps @logto/browser LogtoClient, preserving the same public API
 * so existing code (CloudStorage, WelcomeScreen, DrakkarAdapter, etc.)
 * continues to work without changes.
 */

import LogtoClient, { UserScope } from '@logto/browser';

const LOGTO_ENDPOINT = 'https://auth.echoforge.games';
const LOGTO_APP_ID = '8ov2jk768h253t6reeeef';
const CALLBACK_URL = 'https://tileforge.echoforge.games/callback';
const POST_LOGOUT_URL = 'https://tileforge.echoforge.games';

// Clickjacking defense — refuse to run inside iframes
if (typeof window !== 'undefined' && window.top !== window.self) {
  throw new Error('TileForge cannot run inside an iframe');
}

export class EchoForgeAuth {
  constructor() {
    this._logto = new LogtoClient({
      endpoint: LOGTO_ENDPOINT,
      appId: LOGTO_APP_ID,
      scopes: [UserScope.Email, UserScope.CustomData],
    });

    this._user = null;
    this._onAuthChange = [];
    this._onSessionExpired = [];
    this._ready = false;

    // Initialize: check auth state and populate user
    this._initPromise = this._init();
  }

  async _init() {
    try {
      const authenticated = await this._logto.isAuthenticated();
      if (authenticated) {
        await this._syncUser();
      }
    } catch (e) {
      console.warn('[Auth] Init failed:', e);
    }
    this._ready = true;
    this._notify();
  }

  /** Wait for initial auth state to settle (replaces waitForSso). */
  async waitForSso() {
    return this._initPromise;
  }

  /** Sync user object from Logto ID token claims. */
  async _syncUser() {
    try {
      const claims = await this._logto.getIdTokenClaims();
      if (claims) {
        this._user = {
          userId: claims.sub,
          email: claims.email || '',
          displayName: claims.name || claims.username || (claims.email ? claims.email.split('@')[0] : 'User'),
          isPremium: !!claims.custom_data?.isPremium,
          entitlements: claims.custom_data?.entitlements || [],
        };
      }
    } catch (e) {
      console.warn('[Auth] Failed to get claims:', e);
      this._user = null;
    }
  }

  // ── Public API (same interface as before) ──────────────────

  onAuthChange(fn) {
    this._onAuthChange.push(fn);
    return () => { this._onAuthChange = this._onAuthChange.filter(f => f !== fn); };
  }

  onSessionExpired(fn) {
    this._onSessionExpired.push(fn);
    return () => { this._onSessionExpired = this._onSessionExpired.filter(f => f !== fn); };
  }

  /**
   * Start the sign-in flow. Redirects to Logto hosted sign-in page.
   * No arguments needed — Logto handles email/password/social/2FA.
   */
  async signIn() {
    await this._logto.signIn(CALLBACK_URL);
  }

  /**
   * Sign up — Logto uses the same hosted page for both sign-in and sign-up.
   * The user can switch between modes on the Logto page.
   */
  async signUp() {
    await this._logto.signIn(CALLBACK_URL);
  }

  async signOut() {
    this._user = null;
    this._notify();
    await this._logto.signOut(POST_LOGOUT_URL);
  }

  getUser() {
    return this._user;
  }

  isSuperAdmin() {
    return this._user?.email === 'bruno@civongroup.com';
  }

  hasFeature(feature) {
    if (this.isSuperAdmin()) return true;
    return (this._user?.entitlements || []).includes(feature);
  }

  hasMFA() {
    // Logto handles MFA at the identity provider level;
    // if the user signed in, MFA was already verified if required.
    // Return true for authenticated users since MFA is enforced by Logto config.
    return this.isAuthenticated();
  }

  checkFeatureAccess(feature) {
    if (!this.isAuthenticated()) {
      return { allowed: false, reason: 'Sign in required.' };
    }
    if (this.isSuperAdmin()) {
      return { allowed: true };
    }
    if (!this.hasFeature(feature)) {
      return { allowed: false, reason: 'Access not granted. Contact the administrator.' };
    }
    return { allowed: true };
  }

  isAuthenticated() {
    // Synchronous check — relies on _init having run.
    // LogtoClient caches auth state internally after first async check.
    return this._user !== null;
  }

  /**
   * Get access token synchronously. Returns cached token or null.
   * Callers needing a guaranteed-valid token should use getTokenAsync().
   */
  getToken() {
    // LogtoClient doesn't have a sync token getter.
    // Return null — callers should use getTokenAsync() for API calls.
    // This preserves the pattern where getToken() can return null and
    // the caller falls back to getTokenAsync().
    return null;
  }

  /** Get a valid access token, waiting for refresh if needed. */
  async getTokenAsync() {
    try {
      const authenticated = await this._logto.isAuthenticated();
      if (!authenticated) return null;
      // getAccessToken() without a resource returns the opaque token
      // for the default API. Pass a resource if you need a specific API token.
      const token = await this._logto.getAccessToken();
      return token;
    } catch (e) {
      console.warn('[Auth] getAccessToken failed:', e);
      return null;
    }
  }

  getTier() {
    if (!this._user) return 'anonymous';
    if (this._user.isPremium) return 'pro';
    return 'free';
  }

  /**
   * Handle the sign-in callback. Call this from the callback page.
   * After handling, syncs user and notifies listeners.
   */
  async handleCallback() {
    await this._logto.handleSignInCallback(window.location.href);
    const authenticated = await this._logto.isAuthenticated();
    if (authenticated) {
      await this._syncUser();
      this._notify();
    }
  }

  // ── Internal ───────────────────────────────────────────────

  _notify() {
    for (const fn of this._onAuthChange) {
      try { fn(this._user); } catch (e) { console.error('Auth listener error:', e); }
    }
  }
}
